/**
 * Executive Dashboard Service
 *
 * API client for Executive Value Dashboard endpoints.
 *
 * Part of Story 73: Executive Value Dashboard
 */

import { authFetch } from '@/utils/auth-fetch';
import { API_URL } from '@/config/api';

// Types
export interface HeroKPIs {
  completenessScore: number;
  contentPublished: number;
  demandCoverage: number;
  estimatedMonthlyValue: number;
  trends: {
    completeness: number;
    content: number;
    demand: number;
    value: number;
  };
}

export interface CatalogHealth {
  totalProducts: number;
  productsWithIssues: number;
  issuesBySeverity: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  completenessScore: number;
  completenessScoreWeighted: number;
  completenessBreakdown: Array<{
    category: string;
    score: number;
    productCount: number;
  }>;
  criticalAttributesCoverage: Array<{
    attribute: string;
    found: number;
    missing: number;
    partial: number;
  }>;
  researchProgress: {
    total: number;
    researched: number;
    valuesFound: number;
    naConfirmed: number;
    pending: number;
  };
}

export interface ContentPipeline {
  totalGenerated: number;
  byStatus: {
    published: number;
    validated: number;
    draft: number;
    needsReview: number;
    needsResearch: number;
  };
  byType: {
    productAeo: number;
    categoryAeo: number;
    aiArticle: number;
  };
  deploymentRate: number;
  avgTimeToPublish: number;
  weeklyTrend: Array<{
    week: string;
    generated: number;
    published: number;
  }>;
}

export interface RobotObservability {
  healthScore: number;
  totalBotRequests: number;
  botBreakdown: Array<{
    botType: string;
    requests: number;
    percentage: number;
  }>;
  issues: {
    critical: number;
    warning: number;
    byType: Array<{
      type: string;
      count: number;
      affectedUrls: number;
    }>;
  };
  visibility: {
    potentialUrls: number;
    deployedUrls: number;
    visitedUrls: number;
    visitRate: number;
  };
}

export interface CatalogDiversity {
  diversityScore: number;
  totalSkus: number;
  categoryCount: number;
  brandCount: number;
  brandConcentration: {
    topBrands: Array<{
      name: string;
      count: number;
      percentage: number;
    }>;
    concentrationIndex: number;
  };
  priceDistribution: Array<{
    tier: string;
    count: number;
    percentage: number;
    avgPrice: number;
  }>;
  thinCategories: Array<{
    category: string;
    productCount: number;
  }>;
  categoryDepth: Array<{
    level: number;
    count: number;
  }>;
}

export interface DemandSupplyGap {
  demandCoveragePercent: number;
  totalMarketDemand: number;
  coveredDemand: number;
  gaps: Array<{
    categoryName: string;
    demand: number;
    supply: number;
    demandPerProduct: number;
    gapStatus: 'CRITICAL_GAP' | 'HIGH_OPPORTUNITY' | 'OVER_INVESTED' | 'BALANCED';
    cpcAvg: number;
    competitionLevel: string;
    trendYoy: number;
    opportunityValue: number;
  }>;
  quadrantSummary: {
    highDemandHighCoverage: number;
    highDemandLowCoverage: number;
    lowDemandHighCoverage: number;
    lowDemandLowCoverage: number;
  };
  topOpportunities: Array<{
    category: string;
    monthlySearches: number;
    estimatedValue: number;
  }>;
}

export interface ValueQuantification {
  estimatedMonthlyValue: number;
  breakdown: {
    contentCompleteness: number;
    seoTraffic: number;
    demandGaps: number;
    robotHealth: number;
  };
  opportunityLadder: Array<{
    description: string;
    category: string;
    potentialValue: number;
    effort: 'low' | 'medium' | 'high';
  }>;
  configuredParams: {
    aov: number;
    conversionRate: number;
    monthlyTraffic: number;
  };
}

export interface BotAttentionPriority {
  priorityItems: Array<{
    productId: string;
    productName: string;
    category: string;
    url: string;
    botVisits30d: number;
    botTrendPct: number;
    botTypes: string[];
    hasContent: boolean;
    marketDemand: number;
    revenueEstimate: number;
    priorityScore: number;
    priorityStatus: 'PRIORITY' | 'NEXT_BATCH' | 'OPTIMIZED' | 'DISCOVERY_ISSUE' | 'BACKLOG';
  }>;
  summary: {
    priorityCount: number;
    nextBatchCount: number;
    optimizedCount: number;
    discoveryIssueCount: number;
    backlogCount: number;
    total: number;
  };
  trendingCategories: Array<{
    category: string;
    botVisits: number;
    trendPct: number;
    contentCoverage: number;
    productCount: number;
  }>;
  aiAssistantFocus: Array<{
    queryPattern: string;
    botVisits: number;
    coverage: number;
    gap: number;
  }>;
  discoveryProblems: Array<{
    productId: string;
    productName: string;
    hasContent: boolean;
    botVisits: number;
    likelyIssue: string;
  }>;
}

export interface ValueConfig {
  aov: number;
  conversionRate: number;
  monthlyTraffic: number;
  industryVertical: string;
}

export interface ExecutiveDashboardData {
  hero: HeroKPIs;
  catalogHealth: CatalogHealth;
  contentPipeline: ContentPipeline;
  robotObservability: RobotObservability;
  catalogDiversity: CatalogDiversity;
  demandSupplyGap: DemandSupplyGap;
  valueQuantification: ValueQuantification;
  botAttentionPriority: BotAttentionPriority;
  meta: {
    siteId: string;
    siteName: string;
    generatedAt: string;
    dataFreshness: string;
    periodStart: string;
    periodEnd: string;
  };
}

// Service functions
export async function fetchDashboard(
  siteId: string,
  startDate?: string,
  endDate?: string
): Promise<ExecutiveDashboardData> {
  const params = new URLSearchParams({ siteId });
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const res = await authFetch(`${API_URL}/executive/dashboard?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch dashboard: ${res.statusText}`);
  return res.json();
}

export async function fetchHeroKPIs(
  siteId: string,
  startDate?: string,
  endDate?: string
): Promise<HeroKPIs> {
  const params = new URLSearchParams({ siteId });
  if (startDate) params.append('startDate', startDate);
  if (endDate) params.append('endDate', endDate);

  const res = await authFetch(`${API_URL}/executive/hero?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch hero KPIs: ${res.statusText}`);
  return res.json();
}

export async function fetchCatalogHealth(siteId: string): Promise<CatalogHealth> {
  const res = await authFetch(`${API_URL}/executive/catalog-health?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch catalog health: ${res.statusText}`);
  return res.json();
}

export async function fetchDiversity(siteId: string): Promise<CatalogDiversity> {
  const res = await authFetch(`${API_URL}/executive/diversity?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch diversity: ${res.statusText}`);
  return res.json();
}

export async function fetchDemandSupply(siteId: string): Promise<DemandSupplyGap> {
  const res = await authFetch(`${API_URL}/executive/demand-supply?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch demand-supply: ${res.statusText}`);
  return res.json();
}

export async function fetchQuadrantData(siteId: string): Promise<Array<{
  name: string;
  x: number;
  y: number;
  status: string;
  cpc: number;
}>> {
  const res = await authFetch(`${API_URL}/executive/demand-supply/quadrant?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch quadrant data: ${res.statusText}`);
  return res.json();
}

export async function fetchValue(siteId: string): Promise<ValueQuantification> {
  const res = await authFetch(`${API_URL}/executive/value?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch value: ${res.statusText}`);
  return res.json();
}

export async function fetchValueConfig(siteId: string): Promise<ValueConfig> {
  const res = await authFetch(`${API_URL}/executive/value-config?siteId=${siteId}`);
  if (!res.ok) throw new Error(`Failed to fetch value config: ${res.statusText}`);
  return res.json();
}

export async function updateValueConfig(
  siteId: string,
  config: Partial<ValueConfig>
): Promise<ValueConfig> {
  const res = await authFetch(`${API_URL}/executive/value-config`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ siteId, ...config })
  });
  if (!res.ok) throw new Error(`Failed to update value config: ${res.statusText}`);
  return res.json();
}
